import { isRecording } from "../app";
import { CONSOLE_LOG_TYPES, ConsoleLogTypes } from "../types";
import { CONSOLE_MESSAGE_MAX_LENGTH, getNormalizedUrl, truncateString } from "../utils";


export let consoleLogs: Array<object> = []

let consoleLogCaptureTypes: Array<ConsoleLogTypes> = []

export const resetConsoleData = (): void => {
    consoleLogs = []
}

export const isErrorLog = (msg: string): boolean => {
    if (!msg)
        return false;
    else
        return msg.toLowerCase().includes("error") || msg.toLowerCase().includes("fail");
}

const getErrorType = (errorMessage: string): string => {

    if (errorMessage === null) {
        return "null";
    }
    
    if (errorMessage.includes("TypeError")) {
        return "TypeError";
    } else if (
        errorMessage.includes("ReferenceError") ||
        errorMessage.includes("cannot read property") ||
        errorMessage.includes("undefined")
    ) {
        return "ReferenceError";
    } else if (errorMessage.includes("SyntaxError")) {
        return "SyntaxError";
    } else if (errorMessage.includes("RangeError")) {
        return "RangeError";
    } else if (errorMessage.includes("URIError")) {
        return "URIError";
    } else if (errorMessage.includes("AggregateError")) {
        return "AggregateError";
    } else if (errorMessage.includes("EvalError")) {
        return "EvalError";
    } else {
        return "Error";
    }
};

function extractShortMessage(errorMessage: string): string {
    const errorTypes = [
        "TypeError",
        "ReferenceError",
        "SyntaxError",
        "RangeError",
        "URIError",
        "EvalError",
        "AggregateError",
        "ERROR"
    ];

    function getMessageFromIndex(message: string, startIndex: number): string {
        const lineBreakIndex = message.indexOf("\n", startIndex);
        let shortMessage =
            lineBreakIndex === -1
                ? message.substring(startIndex).trim()
                : message.substring(startIndex, lineBreakIndex).trim();

        if (shortMessage.startsWith("ERROR")) {
            shortMessage = shortMessage.toLocaleLowerCase().replace(/^ERROR\s+/, "");
        }
        if (shortMessage.toLocaleLowerCase().startsWith("Fail")) {
            shortMessage = shortMessage.replace(/^Fail\s+/, "");
        }
        return shortMessage;
    }

    const messageIndex = errorMessage.toLowerCase().indexOf("error") !== -1
        ? errorMessage.toLowerCase().indexOf("error")
        : errorMessage.toLowerCase().indexOf("fail");

    if (messageIndex !== -1) {
        const colonIndex = errorMessage.indexOf(":", messageIndex);

        let startIndex;
        colonIndex !== -1 ? startIndex = colonIndex + 1 : startIndex = messageIndex;

        return getMessageFromIndex(errorMessage, startIndex);
    }

    for (const errorType of errorTypes) {
        const errorTypeIndex = errorMessage.indexOf(errorType);

        if (errorTypeIndex === 0) {
            const afterErrorTypeIndex = errorTypeIndex + errorType.length;
            return getMessageFromIndex(errorMessage, afterErrorTypeIndex).trim();
        }

        if (errorTypeIndex > 0) {
            return getMessageFromIndex(errorMessage, 0);
        }
    }

    return getMessageFromIndex(errorMessage, 0);
}
  
const getShortUrlFromFullUrl = (fullUrl: string): string => {
    let shortUrl = fullUrl
        .substr(fullUrl.lastIndexOf("/") + 1)
        .split(":")
        .splice(0, 2)
        .join(":");
    if (shortUrl.lastIndexOf(":") == 0) {
        shortUrl = "(index)" + shortUrl;
    }
    return shortUrl;
}

const getConsoleLocationFromStack = (): object => {
    try {
        throw new Error();
    } catch (ea: any) {
        let fullUrl: string = ea.stack.substr(ea.stack.lastIndexOf("at ") + 3);
        if (fullUrl && fullUrl.length > CONSOLE_MESSAGE_MAX_LENGTH) {
            fullUrl = truncateString(fullUrl);
        }
        const shortUrl = getShortUrlFromFullUrl(fullUrl);
        return { full_url: fullUrl, short_url: shortUrl };
    }
}

const getErrorMessage = (error: Error): string => {
    return `${error?.message} \n ${error?.stack}`
}

const typeString = (arg: any): string => {
    if (arg === undefined) {
        return 'undefined';
    }
    if (arg === null) {
        return 'null';
    }
    if (arg instanceof Error) {
        return getErrorMessage(arg);
    }
    if (Array.isArray(arg)) {
        return `Array(${arg.length})`;
    }
    return String(arg);
}

const typeFloat = (arg: any): string => {
    if (typeof arg !== 'number') return 'NaN';
    return arg.toString();
}

const typeInt = (arg: any): string => {
    if (typeof arg !== 'number') return 'NaN';
    return Math.floor(arg).toString();
}

const typeObject = (arg: any): string => {
    if (arg === undefined) {
        return 'undefined';
    }
    if (arg === null) {
        return 'null';
    }
    if (arg instanceof Error) {
        return getErrorMessage(arg);
    }
    if (Array.isArray(arg)) {
        const length = arg.length;
        const values = arg.slice(0, 10).map(typeString).join(', ');
        return `Array(${length})[${values}]`;
    }
    if (typeof arg === 'object') {
        const res = [];
        let i = 0;
        for (const k in arg) {
            if (++i === 10) {
                break;
            }
            const v = arg[k];
            res.push(k + ': ' + typeString(v));
        }
        return '{' + res.join(', ') + '}';
    }
    return arg.toString();
}

const getConsoleValue = (args: any[]): string => {
    try {
        if (typeof args[0] === 'string') {
            args.unshift(
                args.shift().replace(/%(o|s|f|d|i)/g, (s: string, t: string): string => {
                    const arg = args.shift();
                    if (arg === undefined) return s;
                    switch (t) {
                        case 'o':
                            return typeObject(arg);
                        case 's':
                            return typeString(arg);
                        case 'f':
                            return typeFloat(arg);
                        case 'd':
                        case 'i':
                            return typeInt(arg);
                        default:
                            return s;
                    }
                }),
            );
        }

        let consoleValue = args.map(typeObject).join(' ');
        if (!consoleValue){
            consoleValue = '';
        } else if (consoleValue.length > CONSOLE_MESSAGE_MAX_LENGTH) {
            consoleValue = truncateString(consoleValue);
        }
        return consoleValue;
    } catch (error) {
        return '';
    }
}

const addLog = (type: string, args: unknown[]): void => {
    if (!type) {
        type = 'log'; // Default to 'log' if type is null or undefined
    }
    if (isRecording) {
        const urls: object = getConsoleLocationFromStack();
        let msg = getConsoleValue(args);

        if (msg == null) {
            msg = '';
        }

        const baseLog = {
            type: type,
            datetime: Date().toLocaleString(),
            value: msg,
            epochTime: Math.floor(Date.now() / 1000),
            properties: {
                pageUrl: window.location.href,
                normalizedUrl: getNormalizedUrl(window.location.href),
                pageName: document.title
            },
            ...urls,
        };

        if ((type.toLowerCase() === "log" && isErrorLog(msg) )|| type.toLowerCase() === "error" ) {
            consoleLogs.push({
                ...baseLog,
                extra: {
                    errorType: getErrorType(msg),
                    msg: msg,
                    shortMsg: extractShortMessage(msg),
                }
            });
        } else {
            consoleLogs.push(baseLog);
        }
    }
};

const captureWindowErrors = (msg: string | Event, url: string | undefined, lineNo: number | undefined, columnNo: number | undefined, error: Error | undefined): boolean => {
    let fullUrl = `${url} : ${lineNo} : ${columnNo}`;
    if (fullUrl.length > CONSOLE_MESSAGE_MAX_LENGTH) {
        fullUrl = truncateString(fullUrl);
    }
    const shortUrl: string = getShortUrlFromFullUrl(fullUrl);
    if (typeof msg === 'string') {
        if (msg.length > CONSOLE_MESSAGE_MAX_LENGTH) {
            msg = truncateString(msg);
        }
    } else if (typeof msg === 'object') {
        msg = JSON.stringify(msg);
        if (msg.length > CONSOLE_MESSAGE_MAX_LENGTH) {
            msg = truncateString(msg);
        }
    }

    let windowError = '';
    if (typeof error === 'object') {
        windowError = JSON.stringify(error);
        if (windowError.length > CONSOLE_MESSAGE_MAX_LENGTH) {
            windowError = truncateString(windowError);
        }
    }

    const errorType = getErrorType(msg as string);
    const shortMsg = extractShortMessage(msg as string);
    
    if (isRecording) {
        consoleLogs.push({
            type: "exception",
            datetime: Date().toLocaleString(),
            value: msg,
            extra: {
                errorType: errorType,
                msg: msg,
                shortMsg: shortMsg,
                url: url,
                normalizedUrl: getNormalizedUrl(url?.toString() || ''),
                lineNo: lineNo,
                columnNo: columnNo,
                error: windowError,
            },
            epochTime: Math.floor(Date.now() / 1000),
            full_url: fullUrl,
            short_url: shortUrl,
        });
    }
    return false;
};

const trackConsole = (console: Console): void => {
    const consoleTypes: string[] = consoleLogCaptureTypes;
    consoleTypes.forEach((type) => {
        const bindConsole = (console as any)[type];
        (console as any)[type] = function (...args: unknown[]): void {
            bindConsole.apply(this, args);
            addLog(type, args);
        };
    });

    //to handle window errors like syntax error etc..
    window.onerror = captureWindowErrors
}


// This is to ensure that the window methods are not overridden multiple times.
let isCollectLogInitialized = false;

export const initalizeConsoleLog = (captureTypes: Array<ConsoleLogTypes>): void => {
    if (isCollectLogInitialized) {
        return
    }
    // set all capture console logs types if all is passed
    if (captureTypes.includes(CONSOLE_LOG_TYPES.ALL)) {
        captureTypes = Object.values(CONSOLE_LOG_TYPES).filter((type) => type !== CONSOLE_LOG_TYPES.ALL);
    }
    consoleLogCaptureTypes = captureTypes;
    trackConsole(window.console);
    isCollectLogInitialized = true;
}
